import { SkyButton, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useEffect, useLayoutEffect, useRef, useState, type FocusEvent } from "react";
import {
  useNotice,
  useNoticeState,
  visibleNotices,
  type HoldReason,
  type Notice,
  type NoticeAction,
} from "@/lib/feedback/NoticeProvider";

/** How long an action's `doneLabel` ("Copied") replaces its label. */
export const DONE_LABEL_MS = 2_000;

/** `AppShell`'s `<main>`, where focus goes when the last focused notice is dismissed. */
const MAIN_CONTENT_ID = "main-content";

/**
 * Where page notices render: in flow, between the page header and `<main>`
 * (`App.tsx` `AppShell`), outside the scroll container, so a notice is visible
 * whatever the scroll position and never covers a control (ux-s10.md §3).
 *
 * Announcing: exactly ONE element announces each notice. Two `sr-only` live lines
 * are always mounted here (an inserted `role=status` element is not reliably
 * announced), one polite and one assertive for errors, and each receives the
 * notice's text once, when it is posted. `SkyMessageBar` hard-codes `role` and
 * `aria-live` on its root by intent with no prop to turn them off, so each visible
 * bar has both removed through its forwarded ref before paint; otherwise a screen
 * reader would read every notice twice.
 *
 * Esc does not dismiss (it belongs to dialogs), and posting never moves focus.
 * A notice that leaves while it holds focus is the one exception, whatever made
 * it leave: its Dismiss button, its own action (the saved notice's "Import now"
 * dismisses the notice it sits in), its timer, a replacement or a route change.
 * Its bar unmounts, so focus goes to the next notice's dismiss button, or to
 * `<main>` (`AppShell` gives `#main-content` tabIndex -1), rather than falling to
 * `<body>`. Focus that has already landed somewhere else is left alone.
 */
export function NoticeRegion() {
  const { notices, announcements, hold } = useNoticeState();
  const { dismiss } = useNotice();
  const regionRef = useRef<HTMLDivElement>(null);
  // The notice focus is inside, recorded on focus and cleared when focus moves out
  // while the bar is still mounted. A bar that unmounts under focus fires no blur
  // React sees, so the id survives the removal and the effect below can act on it.
  const focusedId = useRef<string | null>(null);
  const shown = visibleNotices(notices);
  const shownIds = shown.map((notice) => notice.id).join(" ");

  useLayoutEffect(() => {
    const id = focusedId.current;
    if (id === null || shown.some((notice) => notice.id === id)) return;
    focusedId.current = null;
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    const next = regionRef.current?.querySelector<HTMLElement>(
      '[data-testid="notice"] button[aria-label="Dismiss message"]',
    );
    (next ?? document.getElementById(MAIN_CONTENT_ID))?.focus();
    // `shown` is derived from `shownIds`' notices; the ids are the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownIds]);

  return (
    <div ref={regionRef} data-testid="notice-region" className="flex shrink-0 flex-col">
      {shown.map((notice) => (
        <NoticeBar
          key={notice.id}
          notice={notice}
          onDismiss={() => dismiss(notice.id)}
          onFocusChange={(focused) => {
            if (focused) focusedId.current = notice.id;
            else if (focusedId.current === notice.id) focusedId.current = null;
          }}
          onHold={(reason, held) => hold(notice.id, reason, held)}
        />
      ))}
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="notice-live-polite"
      >
        {announcements.polite ? (
          <span key={announcements.polite.noticeId}>{announcements.polite.text}</span>
        ) : null}
      </div>
      <div
        className="sr-only"
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        data-testid="notice-live-assertive"
      >
        {announcements.assertive ? (
          <span key={announcements.assertive.noticeId}>{announcements.assertive.text}</span>
        ) : null}
      </div>
    </div>
  );
}

function NoticeBar({
  notice,
  onDismiss,
  onFocusChange,
  onHold,
}: {
  notice: Notice;
  onDismiss: () => void;
  /** Focus entered this notice (true) or moved out of it while it is mounted (false). */
  onFocusChange: (focused: boolean) => void;
  onHold: (reason: HoldReason, held: boolean) => void;
}) {
  const barRef = useRef<HTMLDivElement>(null);

  // Runs after every render of this bar, before paint. React only writes an
  // attribute again when its value changes, and the bar's `role` and `aria-live`
  // follow `intent`, which is fixed for a notice's lifetime (a replacement by key
  // is a new notice and a new bar), so once removed they stay removed.
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    bar.removeAttribute("role");
    bar.removeAttribute("aria-live");
  });

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && event.currentTarget.contains(next)) return;
    onFocusChange(false);
    onHold("focus", false);
  };

  return (
    <div
      data-testid="notice"
      data-notice-id={notice.id}
      data-notice-intent={notice.intent}
      onMouseEnter={() => onHold("hover", true)}
      onMouseLeave={() => onHold("hover", false)}
      onFocus={() => {
        onFocusChange(true);
        onHold("focus", true);
      }}
      onBlur={onBlur}
    >
      <SkyMessageBar
        ref={barRef}
        banner
        layout="auto"
        intent={notice.intent}
        title={notice.title ?? undefined}
        message={notice.message}
        testId={`notice-bar-${notice.intent}`}
        actions={
          notice.action ? <NoticeActionButton action={notice.action} noticeId={notice.id} /> : null
        }
        onDismiss={onDismiss}
      />
    </div>
  );
}

function NoticeActionButton({ action, noticeId }: { action: NoticeAction; noticeId: string }) {
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!done) return;
    const handle = setTimeout(() => setDone(false), DONE_LABEL_MS);
    return () => clearTimeout(handle);
  }, [done]);

  return (
    <SkyButton
      size="small"
      appearance="secondary"
      testId="notice-action"
      onClick={async () => {
        // The action reports its own failure (an "Import now" mutation posts its
        // error notice); a rejection here only means the button keeps its label.
        let outcome: void | boolean;
        try {
          outcome = await action.onClick(noticeId);
        } catch {
          outcome = false;
        }
        if (action.doneLabel && outcome !== false) setDone(true);
      }}
    >
      {done && action.doneLabel ? action.doneLabel : action.label}
    </SkyButton>
  );
}
